import { Link } from 'react-router-dom';
import { MessageSquare, PenLine, TrendingUp, Brain, Shield, ArrowRight, Check, Sparkles } from 'lucide-react';

export function FeaturesPage() {
  return (
    <div className="bg-white pt-20">
      <section className="py-24 bg-gradient-to-b from-[#FDF8F4] to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="inline-block text-coral-600 font-medium text-sm tracking-wide uppercase mb-4">
            How it works
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6 leading-tight">
            Three simple tools
            <span className="block text-coral-500">for a clearer life</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Journal, coach, and track your progress. That's it.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-coral-100 rounded-full text-coral-700 text-sm font-medium mb-6">
                <PenLine className="h-4 w-4" />
                <span>Journal</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-6">
                Write what's on your mind
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Quick check-ins when you need them. No pressure to write a novel. Just a moment with yourself to process what's happening.
              </p>

              <div className="space-y-4">
                {[
                  'Smart prompts when you\'re stuck',
                  'Tag entries by topic or mood',
                  'See patterns over time',
                  'Private and encrypted'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3.5 w-3.5 text-sage-600" />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#FDFBF7] rounded-3xl p-8 shadow-xl border border-slate-100">
              <div className="flex items-center justify-between mb-6">
                <span className="font-medium text-slate-900">Today</span>
                <span className="text-xs text-coral-600 font-medium bg-coral-50 px-3 py-1 rounded-full">7 day streak</span>
              </div>
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 border-l-4 border-coral-400 shadow-sm">
                  <p className="text-slate-700 text-sm mb-3">Finally had that conversation with my manager. Felt good to be honest about what I want.</p>
                  <div className="flex gap-2">
                    <span className="px-2.5 py-1 bg-sage-100 text-sage-700 text-xs rounded-full">Career</span>
                    <span className="px-2.5 py-1 bg-coral-100 text-coral-700 text-xs rounded-full">Win</span>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 border-l-4 border-slate-300 shadow-sm">
                  <p className="text-slate-700 text-sm mb-3">Anxious about the presentation tomorrow. Writing it out helps me think clearly.</p>
                  <div className="flex gap-2">
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">Stress</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
            <div className="order-2 lg:order-1 bg-[#FDFBF7] rounded-3xl p-8 shadow-xl border border-slate-100">
              <div className="bg-slate-50 px-6 py-4 -m-8 mb-6 rounded-t-3xl border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-400 to-coral-500 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-medium text-slate-900">Your Coach</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-end">
                  <div className="bg-slate-100 text-slate-900 rounded-2xl rounded-br-md px-4 py-3 max-w-[80%]">
                    <p className="text-sm">I keep procrastinating on my side project. How do I actually start?</p>
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-coral-50 rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%] border border-coral-100">
                    <p className="text-sm text-slate-700 mb-3">Procrastination often comes from feeling overwhelmed. Let's break this down.</p>
                    <p className="text-sm text-slate-700 mb-3">What's the smallest possible first step? Something you could do in 5 minutes?</p>
                    <p className="text-sm text-slate-900 font-medium">Try this: Set a timer for 10 minutes and just work on it. That's it. Notice how you feel after.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-full text-white text-sm font-medium mb-6">
                <MessageSquare className="h-4 w-4" />
                <span>Coach</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-6">
                Get advice that actually helps
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Not generic motivation. Real, personalized guidance based on your situation and history. Your coach remembers every conversation.
              </p>

              <div className="space-y-4">
                {[
                  'Remembers your context',
                  'Adapts to your style',
                  'Creates actionable plans',
                  'Available 24/7'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3.5 w-3.5 text-sage-600" />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sage-100 rounded-full text-sage-700 text-sm font-medium mb-6">
                <TrendingUp className="h-4 w-4" />
                <span>Track</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-6">
                See your progress over time
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Simple weekly scorecards. Rate how you're doing in areas that matter to you. Watch yourself improve week over week.
              </p>

              <div className="space-y-4">
                {[
                  'Weekly check-ins',
                  'Track what matters to you',
                  'See trends over time',
                  'Celebrate your wins'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3.5 w-3.5 text-sage-600" />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#FDFBF7] rounded-3xl p-8 shadow-xl border border-slate-100">
              <div className="flex items-center justify-between mb-6">
                <span className="font-medium text-slate-900">This Week</span>
                <span className="text-sm text-sage-600 font-medium">+12% from last week</span>
              </div>
              <div className="space-y-5">
                {[
                  { label: 'Energy', value: 7, change: '+2', color: 'sage' },
                  { label: 'Focus', value: 6, change: '+1', color: 'coral' },
                  { label: 'Mood', value: 8, change: '+3', color: 'slate' }
                ].map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-slate-700">{item.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-slate-900">{item.value}</span>
                        <span className={`text-xs font-medium ${item.color === 'sage' ? 'text-sage-600' : item.color === 'coral' ? 'text-coral-600' : 'text-slate-600'}`}>{item.change}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${item.color === 'sage' ? 'bg-sage-500' : item.color === 'coral' ? 'bg-coral-500' : 'bg-slate-600'}`}
                        style={{ width: `${item.value * 10}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              What makes VZMO different
            </h2>
            <p className="text-lg text-slate-300">
              Built for real life, not just productivity
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Brain,
                title: 'AI that remembers',
                description: 'Your coach knows your history. Every conversation builds on the last.'
              },
              {
                icon: Shield,
                title: 'Private by design',
                description: 'No feeds, no followers. Just you and your thoughts. Always encrypted.'
              },
              {
                icon: Sparkles,
                title: 'Personalized to you',
                description: 'The experience adapts to your goals, style, and what works for you.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
                <div className="w-12 h-12 rounded-xl bg-coral-500/20 flex items-center justify-center mb-5">
                  <feature.icon className="h-6 w-6 text-coral-400" />
                </div>
                <h3 className="text-xl font-heading font-bold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-white to-[#FDF8F4]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-6">
            Ready to get started?
          </h2>
          <p className="text-xl text-slate-600 mb-10">
            Try it free for 7 days. No credit card required.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-slate-900 text-white rounded-full font-semibold text-lg hover:bg-slate-800 transition-all shadow-lg"
          >
            Start free trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
